import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { fetchDraftBoardPlayers, fetchDraftPicksByYear } from '../../Api'
import { DraftPick, DraftPlayer } from '../../Api/Draft/draft.types'
import { DRAFT_YEAR, prevDraftYearsList } from '../../Config/League/league.config'
import { isDeepEqual, isDraftLive } from '../../Utils'

type DraftContextProps = {
  rounds: number[]
  currentYearDraftPicks: DraftPick[]
  prevDraftPicks: { [key: number]: DraftPick[] } | null
  availablePlayers: DraftPlayer[]
  isLoading: boolean
  isLoadingPreviousDraftPicks: boolean
  fetchDraftPicks: (year: number) => Promise<DraftPick[]>
  getTotalRounds: (picks: DraftPick[]) => number[]
  nextTeamToPick: DraftPick | null
}

const DraftContext = createContext<DraftContextProps | null>(null)
export const useDraftContext = () => useContext(DraftContext)!

export const DraftProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [rounds, setRounds] = useState<number[]>([1, 2, 3, 4]) //Default League Rounds
  const [currentYearDraftPicks, setCurrentYearDraftPicks] = useState<DraftPick[]>([])
  const [prevDraftPicks, setPrevDraftPicks] = useState<{
    [key: number]: DraftPick[]
  } | null>(null)
  const [availablePlayers, setAvailablePlayers] = useState<DraftPlayer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingPreviousDraftPicks, setIsLoadingPreviousDraftPicks] =
    useState<boolean>(true)
  const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null)
  const nextTeamToPick = useMemo(() => {
    if (!isDraftLive() || !currentYearDraftPicks?.length) return null
    const pendingPicks = currentYearDraftPicks.filter((pick) => pick?.playerName === null)
    if (pendingPicks.length === 0) return null
    const nextPick = pendingPicks[0]
    return nextPick
  }, [currentYearDraftPicks])

  useEffect(() => {
    fetchDraftData()

    let checkIntervalId: NodeJS.Timeout | null = null
    checkAndManagePolling()
    checkIntervalId = setInterval(checkAndManagePolling, 20000)

    return () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId)
      }
      if (checkIntervalId) {
        clearInterval(checkIntervalId)
      }
    }
  }, [pollingIntervalId])

  const getTotalRounds = useCallback((picks: DraftPick[]) => {
    if (picks.length < 1) return [1, 2, 3, 4] //Default League Rounds
    const uniqueRounds = picks.reduce<number[]>((acc, pick) => {
      if (acc.length === 0 || acc[acc.length - 1] !== pick.round) {
        acc.push(pick.round)
      }
      return acc
    }, [])
    return uniqueRounds
  }, [])

  const fetchDraftData = async () => {
    const [picks] = await Promise.all([
      fetchDraftPicks(DRAFT_YEAR),
      fetchPlayersOnDraftBoard(DRAFT_YEAR),
    ])
    const totalRounds = getTotalRounds(picks)
    setRounds(totalRounds)
    setIsLoading(false)
    fetchPreviousDraftPicks()
  }

  const fetchDraftPicks = async (year: number) => {
    const picks = await fetchDraftPicksByYear(year)
    if (!picks || picks.length < 1) {
      setIsLoading(false)
      return []
    }
    if (!isDeepEqual(picks, currentYearDraftPicks)) {
      setCurrentYearDraftPicks(picks)
    }
    setIsLoading(false)
    return picks
  }

  const handlePollingFetch = async () => {
    if (!isDraftLive()) {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId)
        setPollingIntervalId(null)
      }
      return
    }

    try {
      console.log('Fetching draft picks...')
      await fetchDraftPicks(DRAFT_YEAR)
    } catch (error) {
      console.error('Error fetching draft picks during polling:', error)
    }
  }

  const checkAndManagePolling = () => {
    const liveDraft = isDraftLive()
    if (liveDraft && !pollingIntervalId) {
      console.log('Draft is live. Starting polling...')
      handlePollingFetch()
      const intervalId = setInterval(handlePollingFetch, 30000)
      setPollingIntervalId(intervalId)
    } else if (!liveDraft && pollingIntervalId) {
      console.log('Draft ended or not started. Stopping polling.')
      clearInterval(pollingIntervalId)
      setPollingIntervalId(null)
    }
  }

  const fetchPlayersOnDraftBoard = async (year: number) => {
    const players = await fetchDraftBoardPlayers(year)
    if (!players || players.length < 1) {
      setIsLoading(false)
      return
    }
    setAvailablePlayers(players.sort((a, b) => a.overallRank - b.overallRank))
  }

  const fetchPreviousDraftPicks = async () => {
    let previousPicks = { ...prevDraftPicks }
    await Promise.all(
      prevDraftYearsList.map(async (year) => {
        const picks = await fetchDraftPicksByYear(year)
        if (!picks || picks.length < 1) {
          previousPicks[year] = []
          return
        }
        previousPicks[year] = picks
        return
      })
    )
    setPrevDraftPicks(previousPicks)
    setIsLoadingPreviousDraftPicks(false)
  }

  return (
    <DraftContext.Provider
      value={{
        rounds,
        currentYearDraftPicks,
        prevDraftPicks,
        availablePlayers,
        isLoading,
        isLoadingPreviousDraftPicks,
        nextTeamToPick,
        fetchDraftPicks,
        getTotalRounds,
      }}
    >
      {children}
    </DraftContext.Provider>
  )
}
